import { useEffect, useState } from 'react';

import {
  PresenceUser,
  DealPresenceDocument,
  DealPresencesDocument,
} from '@/lib/graphql/generated/graphql';
import { useQuery, useSubscription } from '@apollo/client/react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Subscribes to real-time presence updates for a deal page
 * - First queries for initial data, then subscribes for real-time updates
 * - Receives updates when users join/leave
 * - Use this on pages where you want to display who's present (/choose)
 *
 * @param dealId - The ID of the deal being viewed
 * @returns Object containing:
 *   - otherUsers: Array of other users currently viewing the page (excludes current user)
 *   - allUsers: Array of all users including current user
 *   - loading: True when initial query is loading
 */
export function usePresenceSubscription(dealId: string) {
  const { user } = useAuth0();
  const [users, setUsers] = useState<Array<PresenceUser>>([]);

  // Query for initial presence data
  const { data: queryData, loading } = useQuery(DealPresencesDocument, {
    variables: { dealId },
    skip: !dealId,
  });

  // Subscribe to real-time presence updates
  const { data: subscriptionData } = useSubscription(DealPresenceDocument, {
    variables: { dealId },
    skip: !dealId,
  });

  // Initialize with query data
  useEffect(() => {
    if (queryData?.dealPresence?.users) {
      setUsers(queryData.dealPresence.users);
    }
  }, [queryData]);

  // Update with subscription data
  useEffect(() => {
    if (subscriptionData?.dealPresence?.users) {
      setUsers(subscriptionData.dealPresence.users);
    }
  }, [subscriptionData]);

  // Filter out current user
  const otherUsers = users.filter((u) => u.userId !== user?.sub);

  return {
    otherUsers,
    loading,
  };
}

export function usePresence(dealId: string) {
  return usePresenceSubscription(dealId);
}
